import React, { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { BaseAudioBookReturn } from '../../../generated/client';
import './Library.css';
import { AudioBookServiceFactory } from '../AudioBookServiceFactory';
import { AudioBookResultsComponent } from './AudioBookResultsComponent';

/**
 * The AudioBookSearchBar does the following:
 * -- queries database to retrieve list of audiobooks based on search terms
 * -- handles filters of search terms
 *
 */

export function AudioBookSearchBar(): JSX.Element {
  const audioBooksService = AudioBookServiceFactory.instance();
  const filterAny = 'Any';
  const filterTitle = 'Title';
  const filterHost = 'Host';
  const filterGenre = 'Genre';
  const [audiobookData, setAudioBookData] = useState<BaseAudioBookReturn[]>([]); // array of audiobooks
  const [keyword, setKeyword] = useState<string>(''); // string to be used to call listAudioBooks
  const filterOptions = [filterAny, filterTitle, filterHost, filterGenre]; // options to filter search by
  const [searchFilter, setSearchFilter] = useState<string>(filterAny); // current search filter

  // handles event when user clicks the search button to search for an audiobook
  const search = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.target as HTMLFormElement;
    const input = form.querySelector('#searchText') as HTMLInputElement;
    setAudioBookData([]); // clears data from pervious search
    setKeyword(input.value); // sets keyword as user input
  };

  // handles event when user changes the filter of an audiobook search
  const handleFilter = (event: ChangeEvent<HTMLSelectElement>) => {
    event.preventDefault();
    setAudioBookData([]); // clears data from pervious search
    setSearchFilter(event.target.value); // sets new filter
  };

  // query to retrieve list of books each time the search term or filter changes
  useEffect(() => {
    (async () => {
      const query = keyword; // most recent user input

      if (query) {
        let response = await audioBooksService.listAudioBooks(
          undefined,
          query,
          undefined,
          undefined,
        ); // most generalized search as initilizer

        if (searchFilter == filterAny) {
          response = await audioBooksService.listAudioBooks(undefined, query, undefined, undefined);
        } // most generalized search - any related title, author, or 'tag'

        if (searchFilter == filterHost) {
          response = await audioBooksService.listAudioBooks(query, undefined, undefined, undefined);
        } // search by person listed as author

        if (searchFilter == filterTitle) {
          response = await audioBooksService.listAudioBooks(undefined, undefined, undefined, query);
        } // search by title

        if (searchFilter == filterGenre) {
          response = await audioBooksService.listAudioBooks(undefined, undefined, query, undefined);
        } // search by 'tag' aka genre

        setAudioBookData(response); // sets audiobook data based on filter and keyword search
      }
    })();
  }, [keyword, searchFilter, audioBooksService]);

  return (
    <div className='wrap'>
      <div className='searchBar'>
        <select id='dropdown' className='dropDown' onChange={handleFilter}>
          {filterOptions.map(opt => (
            <option key={opt}> {opt} </option>
          ))}
          ;
        </select>
        <form className='searchForm' onSubmit={event => search(event)}>
          <input id='searchText' type='text' className='searchTerm' />
          <button className='searchButton'>Search</button>
        </form>
      </div>
      {searchFilter == filterHost ? (
        <text>
          Note: searching by host will result in a list of episodes, not audiobook collections{' '}
        </text>
      ) : (
        ''
      )}
      <div className='resultsContainer'>
        {keyword && (
          <div className='resultsDescription'>
            <b>
              {' '}
              {audiobookData.length != 0
                ? `Showing ${audiobookData.length} results for ${keyword} ...`
                : `Searching for ${keyword} ...`}
            </b>
          </div>
        )}
        {audiobookData.map(podcast => (
          <AudioBookResultsComponent
            key={podcast.id}
            podcast={podcast}
            audioBookService={audioBooksService}></AudioBookResultsComponent>
        ))}
      </div>
    </div>
  );
}
